<template>
  <v-card elevation="0" class="participant-list-item mb-6 rounded-lg">
    <div class="participant-list-item__main-data px-3 py-1">
      <a
        @click="$emit('navigate-to-participant', participantId)"
        class="participant-list-item__column participant-list-item__column--avatar"
      >
        <profile-preview-image
          :profileId="profileId"
          :avatarSize="48"
          :avatarClasses="[
            'participant-list-item__block--avatar',
            'avatar',
            'rounded-circle',
          ]"
          :imageClasses="['participant-list-item__avatar']"
        />
        <div class="participant-list-item__block">
          <p class="participant-list-item__fullname">
            {{ participantFullname }}
          </p>
          <p class="participant-list-item__status">
            <span
              class="participant-list-item__label participant-list-item__active"
              >{{ isActive ? 'Active' : 'Inactive' }}</span
            >
            <span
              v-if="isLite"
              class="participant-list-item__label participant-list-item__lite"
              >Lite</span
            >
            <span
              class="participant-list-item__label participant-list-item__registration-date"
              >{{ regDate | moment('MMMM Do, YYYY') }}</span
            >
          </p>
        </div>
      </a>
      <div class="participant-list-item__block">
        <p
          v-if="consultantFullname"
          class="participant-list-item__consultant-fullname"
        >
          {{ consultantFullname }}
        </p>
        <p v-if="consultantFullname" class="participant-list-item__label">
          Consultant
        </p>
      </div>
      <div class="participant-list-item__block">
        <a
          v-if="phone"
          class="participant-list-item__phone"
          :href="`tel:${phone}`"
          >{{ phoneUsFormat(phone) }}</a
        >
      </div>
      <div class="participant-list-item__block">
        <p
          v-if="lastUpdatedSwap"
          class="participant-list-item__last-updated-swap"
        >
          {{ lastUpdatedSwap | moment('MM/DD/YYYY hh:mm A') }}
        </p>
        <p v-if="lastUpdatedSwap" class="participant-list-item__label">
          {{ tenantTexts.participantsDashboard.lastUpdatedSwap }}
        </p>
      </div>
      <div
        class="participant-list-item__block participant-list-item__notes-wrapper"
      >
        <v-tooltip open-delay="200" top>
          <template v-slot:activator="{ on, attrs }">
            <div class="participant-list-item__notes" v-bind="attrs" v-on="on">
              {{ notes }}
            </div>
          </template>
          <span class="participant-list-item__notes-tooltip">{{ notes }}</span>
        </v-tooltip>
        <v-btn
          icon
          color="accent"
          elevation="0"
          @click="$emit('navigate-to-participant', participantId)"
        >
          <v-icon>mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </div>
    <div class="participant-list-item__detail-data px-3 py-1">
      <div class="participant-list-item__block">
        <a
          @click="$emit('navigate-to-participant', participantId, 'routines')"
          class="participant-list-item__block-item"
        >
          <p
            :class="
              `participant-list-item__label participant-list-item__label--indicator-${respondedStatus(
                'routines',
              )}`
            "
          >
            {{ tenantTexts.participantsDashboard.routines }}:
            <strong>{{ routinesCompleted }}/{{ routinesTotal }}</strong>
          </p>
        </a>
        <v-tooltip open-delay="200" top>
          <template v-slot:activator="{ on, attrs }">
            <a
              @click="
                $emit('navigate-to-participant', participantId, 'courses')
              "
              v-bind="attrs"
              v-on="on"
              class="participant-list-item__block-item"
            >
              <p :class="`participant-list-item__label`">
                Courses:
                <strong
                  >{{ allEnrolledCourses }}/{{ allAvailableCourses }}</strong
                >
              </p>
            </a>
          </template>
          <span class="participant-list-item__courses-tooltip"
            >Enrolled Courses {{ allEnrolledCourses }} / Available Courses
            {{ allAvailableCourses }}<br />All time, not affected by the date
            filter</span
          >
        </v-tooltip>
      </div>
      <div class="participant-list-item__block">
        <v-tooltip open-delay="200" top>
          <template v-slot:activator="{ on, attrs }">
            <a
              @click="
                $emit('navigate-to-participant', participantId, 'routines')
              "
              v-bind="attrs"
              v-on="on"
              class="participant-list-item__block-item"
            >
              <p
                class="participant-list-item__label participant-list-item__rating"
              >
                Rating&nbsp;<rating-stars
                  :rating="ratingTotalRaw"
                  :size="12"
                />&nbsp;<strong>{{ ratingTotal }}/5</strong>
              </p>
            </a>
          </template>
          <span
            >Rating Day: {{ ratingDay }}/5<br />Rating Actions:
            {{ ratingActions }}/5<br />Rating Total: {{ ratingTotal }}/5</span
          >
        </v-tooltip>
        <a
          @click="$emit('navigate-to-participant', participantId, 'courses')"
          v-bind="attrs"
          v-on="on"
          class="participant-list-item__block-item"
        >
          <p class="participant-list-item__label participant-list-item__rating">
            Rating&nbsp;<rating-stars
              :rating="coursesRating"
              :size="12"
            />&nbsp;<strong>{{ coursesRating }}/5</strong>
          </p>
        </a>
      </div>
      <div class="participant-list-item__block">
        <div
          class="participant-list-item__block-item"
          v-if="journeyEndDate && shouldShowEndDate"
        >
          <p
            :class="
              endDateStatus
                ? `participant-list-item__label participant-list-item__label--indicator-${endDateStatus}`
                : 'participant-list-item__label'
            "
          >
            Journey ends <strong>&nbsp; {{ calculatedEndDate }}</strong>
          </p>
        </div>
        <div class="participant-list-item__block-item">
          <a
            @click="$emit('navigate-to-participant', participantId, 'chat')"
            class="participant-list-item__block"
            v-if="!isCompanyAdmin"
          >
            <p
              :class="
                `participant-list-item__block-item participant-list-item__label participant-list-item__label--indicator-${respondedStatus(
                  'chat',
                )}`
              "
            >
              Unread msg. <strong>&nbsp;{{ unreadChatMessages }}</strong>
            </p>
          </a>
          <v-tooltip open-delay="200" top>
            <template v-slot:activator="{ on, attrs }">
              <a
                @click="
                  $emit('navigate-to-participant', participantId, 'journal')
                "
                class="participant-list-item__block"
                v-bind="attrs"
                v-on="on"
              >
                <p
                  v-if="showJournal"
                  :class="
                    `participant-list-item__block-item participant-list-item__label participant-list-item__label--indicator-${respondedStatus(
                      'journal',
                    )}`
                  "
                >
                  Journal entries <strong>&nbsp;{{ journalEntries }}</strong>
                </p>
              </a>
            </template>
            <span v-if="showJournal"
              >Unresponded entries: {{ unrespondedTotal }}</span
            >
          </v-tooltip>
        </div>
      </div>
      <div class="participant-list-item__block">
        <a
          @click="$emit('navigate-to-participant', participantId, 'swaps')"
          class="participant-list-item__block-item"
        >
          <p
            v-if="showSwaps"
            class="participant-list-item__label participant-list-item__label--with-chips"
          >
            <span class="participant-list-item__label--chips-label">
              {{ tenantTexts.participantsDashboard.swaps }}</span
            >
            <span class="flex">
              <v-chip pill
                ><strong>{{ swapActivitiesTotal }}</strong
                >&nbsp;Added To</v-chip
              ><v-chip pill
                ><strong>{{ swapCompleted }}</strong
                >&nbsp;Completed</v-chip
              ><v-chip pill
                ><strong>{{ swapInProgress }}</strong
                >&nbsp;In progress</v-chip
              ></span
            >
          </p>
        </a>

        <a
          @click="$emit('navigate-to-participant', participantId, 'courses')"
          class="participant-list-item__block-item"
        >
          <p
            v-if="showSwaps"
            class="participant-list-item__label participant-list-item__label--with-chips"
          >
            <span class="participant-list-item__label--chips-label">
              Courses, all time</span
            >
            <span class="flex">
              <v-chip pill
                ><strong>{{ coursesEnrolled }}</strong
                >&nbsp;Enrolled</v-chip
              ><v-chip pill
                ><strong>{{ coursesCompleted }}</strong
                >&nbsp;Completed</v-chip
              ><v-chip pill
                ><strong>{{ coursesInProgress }}</strong
                >&nbsp;In progress</v-chip
              ></span
            >
          </p>
        </a>
      </div>
    </div>
  </v-card>
</template>

<script>
import TenantTextsMixin from '@/mixins/tenantTexts'
import phoneFormat from '@/helpers/phoneFormat.js'
import ProfilePreviewImage from '../profile/ProfilePreviewImage.vue'
import RatingStars from '../exercises/RatingStars.vue'
import { mapGetters, mapState } from 'vuex'
export default {
  components: { ProfilePreviewImage, RatingStars },
  name: 'ParticipantListItem',
  mixins: [TenantTextsMixin],
  props: {
    showSwaps: {
      type: Boolean,
      required: false,
      default: true,
    },
    showJournal: {
      type: Boolean,
      required: false,
      default: true,
    },
    profileId: {
      type: Number,
      require: true,
    },
    participantId: {
      type: Number,
      require: true,
    },
    isActive: {
      type: Boolean,
      require: true,
    },
    firstname: {
      type: String,
      require: true,
    },
    lastname: {
      type: String,
      require: true,
    },
    registrationDate: {
      type: String,
      require: true,
    },
    phone: {
      type: String,
      require: true,
    },
    notes: {
      type: String,
      require: false,
      default: '',
    },
    routinesTotal: {
      type: Number,
      require: false,
      default: 0,
    },
    routinesSkipped: {
      type: Number,
      require: false,
      default: 0,
    },
    routinesCompleted: {
      type: Number,
      require: false,
      default: 0,
    },
    chatId: {
      type: String,
      require: false,
      default: '',
    },
    journalEntries: {
      type: Number,
      require: false,
      default: 0,
    },
    coursesAvailable: {
      type: Number,
      require: false,
      default: 0,
    },
    coursesCompleted: {
      type: Number,
      require: false,
      default: 0,
    },
    coursesEnrolled: {
      type: Number,
      require: false,
      default: 0,
    },
    coursesInProgress: {
      type: Number,
      require: false,
      default: 0,
    },
    swapNew: {
      type: Number,
      require: false,
      default: 0,
    },
    swapInProgress: {
      type: Number,
      require: false,
      default: 0,
    },
    swapCompleted: {
      type: Number,
      require: false,
      default: 0,
    },
    swapActivities: {
      type: Number,
      require: false,
      default: 0,
    },
    swapActivitiesTotal: {
      type: Number,
      require: false,
      default: 0,
    },
    inactiveDays: {
      type: Number,
      require: false,
      default: 0,
    },
    total: {
      type: Number,
      require: false,
      default: 0,
    },
    unrespondedTotal: {
      type: Number,
      require: false,
      default: 0,
    },
    ratingSum: {
      type: Number,
      require: false,
      default: 0,
    },
    ratingCount: {
      type: Number,
      require: false,
      default: 0,
    },
    ratingDayCount: {
      type: Number,
      require: false,
      default: 0,
    },
    ratingDaySum: {
      type: Number,
      require: false,
      default: 0,
    },
    ratingActionsSum: {
      type: Number,
      require: false,
      default: 0,
    },
    ratingActionsCount: {
      type: Number,
      require: false,
      default: 0,
    },
    thumbnailUrl: {
      type: String,
      require: false,
      default: '',
    },
    consultantFirstname: {
      type: String,
      require: false,
      default: '',
    },
    consultantLastname: {
      type: String,
      require: false,
      default: '',
    },
    lastUpdatedSwap: {
      type: String,
      require: false,
      default: '',
    },
    unreadChatMessages: {
      type: Number,
      require: false,
      default: 0,
    },
    coursesRating: {
      type: Number,
      require: false,
      default: 0,
    },
    coursesRatingSum: {
      type: Number,
      require: false,
      default: 0,
    },
    coursesRatingCount: {
      type: Number,
      require: false,
      default: 0,
    },
    primaryChannel: {
      type: String,
      required: false,
      default: undefined,
    },
    journeyEndDate: {
      type: String,
      require: false,
      default: '',
    },
  },
  data: () => ({
    isLoading: false,
  }),
  computed: {
    ...mapGetters('userStates', ['getRolename']),
    ...mapState('dictionariesStates', ['dictionaryPracticeUserRole']),
    ratingTotalRaw() {
      if (this.ratingCount === 0) return 0
      return parseFloat(this.ratingSum / this.ratingCount)
    },
    ratingTotal() {
      if (this.ratingCount === 0) return 0
      return parseFloat(this.ratingSum / this.ratingCount).toFixed(1)
    },
    ratingDay() {
      if (this.ratingDayCount === 0) return 0
      return parseFloat(this.ratingDaySum / this.ratingDayCount).toFixed(1)
    },
    ratingActions() {
      if (this.ratingActionsCount === 0) return 0
      return parseFloat(
        this.ratingActionsSum / this.ratingActionsCount,
      ).toFixed(1)
    },
    regDate() {
      // use moment
      return this.registrationDate
    },
    participantFullname() {
      let fullname = this.firstname
      if (fullname.length > 0) fullname += ' '
      fullname += this.lastname
      return fullname
    },
    consultantFullname() {
      let fullname = this.consultantFirstname
      if (fullname.length > 0) fullname += ' '
      fullname += this.consultantLastname
      return fullname
    },
    isCompanyAdmin() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Administrator
      )
    },
    allEnrolledCourses() {
      return (
        (this.coursesCompleted || 0) +
        (this.coursesEnrolled || 0) +
        (this.coursesInProgress || 0)
      )
    },
    allAvailableCourses() {
      return (this.allEnrolledCourses || 0) + (this.coursesAvailable || 0)
    },
    today() {
      return this.$moment().startOf('day')
    },
    isLite() {
      return ['SMS', 'EMAIL'].includes(this.primaryChannel)
    },
    diffDays() {
      if (!this.journeyEndDate) return null
      return this.$moment(this.journeyEndDate).diff(
        this.$moment(this.today).subtract(1, 'day'),
        'days',
      )
    },
    calculatedEndDate() {
      if (this.diffDays === null) return ''
      if (this.diffDays === 0) return 'Today'
      if (this.diffDays === 1) return '1 day left'
      return `${this.diffDays} days left`
    },
    shouldShowEndDate() {
      if (this.diffDays === null) return false
      return this.diffDays >= 0
    },
    endDateStatus() {
      if (this.diffDays === null) return ''
      if (this.diffDays <= 3) return 'error'
      if (this.diffDays <= 7) return 'warning'
      if (this.diffDays > 7) return 'responded'
      return ''
    },
  },
  methods: {
    phoneUsFormat: phoneFormat.phoneUsFormat,
    handleLoadStart() {
      this.isLoading = true
    },
    handleLoad() {
      this.isLoading = false
    },
    respondedStatus(type = '') {
      let status = ''
      switch (type) {
        case 'routines':
          if (!this.routinesTotal) status = 'no-data'
          else if (
            this.routinesCompleted !== this.routinesTotal &&
            this.routinesTotal
          )
            status = 'not-responded'
          else if (!this.routinesCompleted === this.routinesTotal)
            status = 'responded'
          break
        case 'chat':
          if (this.unreadChatMessages > 0) status = 'not-responded'
          else status = 'responded'
          break
        case 'journal':
          if (!this.journalEntries) status = 'no-data'
          else if (this.unrespondedTotal > 0 && this.journalEntries)
            status = 'not-responded'
          else if (!this.unrespondedTotal && this.journalEntries)
            status = 'responded'
          break
        case 'end-date':
          if (!this.endDate) status = 'no-data'
          else status = this.endDateStatus
          break
        default:
          status = ''
      }

      return status
    },
  },
}
</script>

<style lang="scss">
.participant-list-item {
  min-width: 800px;

  &__rating,
  &__main-data,
  &__detail-data {
    display: flex;
    align-items: center;
  }

  &__main-data,
  &__detail-data {
    flex-wrap: wrap;

    > .participant-list-item__block,
    > .participant-list-item__column:not(.participant-list-item__column--avatar) {
      min-width: 150px;
      @media (max-width: 1400px) {
        max-width: 160px;
        min-width: auto;
      }
      flex: 1;
      padding: 0.25 * $gutter;

      &:first-child {
        min-width: 275px;
        @media (max-width: 1400px) {
          max-width: 200px;
          min-width: auto;
        }
      }

      &:last-child {
        justify-items: flex-end;
        min-width: 415px;
      }
    }
  }

  &__main-data {
    margin: -0.25 * $gutter;
  }

  &__detail-data {
    align-items: flex-start;
    margin: -0.25 * $gutter;
    border-top: 1px solid $bh-gray--4;

    .participant-list-item__block-item {
      display: flex !important;
      align-items: center;
      min-height: 40px;
    }
  }

  &__column {
    display: flex;
  }

  &__block {
    flex: 1;
    color: inherit !important;

    p {
      margin-bottom: 0;
    }

    &--avatar {
      margin-right: 0.5 * $gutter;
    }

    .v-chip {
      font-size: 12px;
      cursor: inherit;
      margin-left: 0.5 * $gutter;
      background-color: $bh-gray--6 !important;
    }
  }

  &__phone {
    color: inherit !important;
  }

  &__registration-date {
    color: $bh-gray;
  }

  &__active {
    margin-right: 0.5 * $gutter;
  }

  &__lite {
    margin-right: 0.5 * $gutter;
    padding: 0 6px;
    border-radius: 4px;
    background: $bh-gray;
    color: #fff;
  }

  &__active,
  &__fullname {
    color: #000;
  }

  &__notes {
    &-wrapper {
      display: flex;
      align-items: center;
    }

    &-tooltip {
      max-width: 800px;
      display: inline-block;
    }

    flex: 1;
    font-size: 12px;
    line-height: 16px;
    max-height: 48px;
    position: relative;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }

  &__label {
    font-size: 12px;
    line-height: 22px;
    color: $bh-black;

    strong {
      color: #000;
    }

    &--with-chips {
      display: flex;
      align-items: center;
    }

    &--chips-label {
      min-width: 85px;
    }
  }

  .rating-stars .v-icon {
    margin-right: 5px;

    &::before {
      color: $bh-yellow;
    }
  }

  &__detail-data {
    .participant-list-item__label--indicator {
      &-responded,
      &-no-data,
      &-not-responded,
      &-warning,
      &-error {
        display: inline-block;
        padding-left: 16px;
        position: relative;

        &::before {
          content: ' ';
          width: 8px;
          height: 8px;
          border-radius: 4px;
          position: absolute;
          top: calc(50% - 4px);
          left: 0;
          background-color: $bh-blue;
        }
      }
      &-responded::before {
        background-color: $bh-green--light;
      }
      &-warning::before {
        background-color: $bh-yellow--light;
      }
      &-no-data::before {
        background-color: $bh-gray--4;
      }
      &-error::before {
        background-color: $bh-red--light;
      }
    }
  }
}
</style>
